import { type MutableRefObject, useEffect } from 'react'
import { computeSafariPinchZoom } from '../helpers/safari-pinch-zoom'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { PendingCtrlWheelAnchor } from '../types/pending-ctrl-wheel-anchor'

/** Safari / WebKit trackpad pinch zoom, which arrives as gesture events rather than ctrl+wheel. */
export function useSafariPinchZoom({
  scrollRef,
  zoomRef,
  scaleRef,
  onZoomChangeRef,
  pendingCtrlWheelAnchorRef,
  syncRulerScrollTransforms,
  computeContentPointAtClient,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  zoomRef: MutableRefObject<number>
  scaleRef: MutableRefObject<number>
  onZoomChangeRef: MutableRefObject<CenterCanvasConfig['onZoomChange']>
  pendingCtrlWheelAnchorRef: MutableRefObject<PendingCtrlWheelAnchor | null>
  syncRulerScrollTransforms: () => void
  computeContentPointAtClient: (
    clientX: number,
    clientY: number,
    atScale: number
  ) => { contentX: number; contentY: number } | null
}) {
  /** Safari / WebKit: trackpad pinch uses gesture events (not ctrl+wheel). */
  useEffect(() => {
    const scroller = scrollRef.current

    if (!scroller) return
    if (typeof window === 'undefined' || !('gesturestart' in window)) return

    let lastPinchScale = 1

    const onGestureStart = (e: Event) => {
      e.preventDefault()
      lastPinchScale = 1
    }

    const onGestureChange = (e: Event) => {
      e.preventDefault()
      const ge = e as unknown as { scale: number; clientX: number; clientY: number }
      const rawRatio = ge.scale / lastPinchScale

      lastPinchScale = ge.scale

      const current = zoomRef.current === 0 ? scaleRef.current : zoomRef.current
      const next = computeSafariPinchZoom(current, rawRatio)

      if (next === +current.toFixed(3)) {
        requestAnimationFrame(() => syncRulerScrollTransforms())

        return
      }

      if (zoomRef.current !== 0) {
        const point = computeContentPointAtClient(ge.clientX, ge.clientY, current)

        if (point) {
          pendingCtrlWheelAnchorRef.current = {
            contentX: point.contentX,
            contentY: point.contentY,
            clientX: ge.clientX,
            clientY: ge.clientY,
            to: next,
          }
        }
      }

      onZoomChangeRef.current?.(next)
      requestAnimationFrame(() => syncRulerScrollTransforms())
    }

    const onGestureEnd = (e: Event) => {
      e.preventDefault()
      lastPinchScale = 1
    }

    scroller.addEventListener('gesturestart', onGestureStart, { passive: false })
    scroller.addEventListener('gesturechange', onGestureChange, { passive: false })
    scroller.addEventListener('gestureend', onGestureEnd, { passive: false })

    return () => {
      scroller.removeEventListener('gesturestart', onGestureStart)
      scroller.removeEventListener('gesturechange', onGestureChange)
      scroller.removeEventListener('gestureend', onGestureEnd)
    }
  }, [
    syncRulerScrollTransforms,
    computeContentPointAtClient,
    scrollRef,
    zoomRef,
    scaleRef,
    onZoomChangeRef,
    pendingCtrlWheelAnchorRef,
  ])
}
